import { useState } from 'react';

import type { MateriaOutputType } from '@shared/dto';

import { useApiMutation } from '../../../hooks/use-api';
import { matriculasApi } from '../../../api/matriculas.api';
import { usePainelSnapshot } from '../../../hooks/use-painel-snapshot';
import { Button } from '../../ui/button';
import { MatriculaDraftCard, type MatriculaDraft } from './matricula-draft-card';
import { programacaoSemanalVazia } from './programacao-semanal-grid';
import { programacaoValida } from './programacao-semanal';

function draftVazio(): MatriculaDraft {
  return {
    key: 'nova',
    professorId: '',
    materiaId: '',
    estagio: '',
    tipoAtendimento: 'REGULAR',
    observacoes: '',
    programacao: programacaoSemanalVazia(),
  };
}

/** "+ Nova matrícula" num aluno que já existe — persiste assim que salva, sem estágio de rascunho. */
export function NovaMatriculaForm({
  alunoId,
  materias,
  onCriada,
  onCancelar,
}: {
  alunoId: string;
  materias: MateriaOutputType[];
  onCriada: () => void;
  onCancelar: () => void;
}) {
  const [draft, setDraft] = useState<MatriculaDraft>(draftVazio());
  const { mutate: criarMatricula, loading: criandoMatricula } = useApiMutation(matriculasApi.criar);
  const [erro, setErro] = useState<string | null>(null);
  const { dados } = usePainelSnapshot();
  const professores = dados?.professores ?? [];

  async function aoSalvar() {
    setErro(null);
    if (!draft.materiaId || !draft.professorId) {
      setErro('Selecione a disciplina e o professor.');
      return;
    }
    const professor = professores.find((item) => item.id === draft.professorId);
    if (!professor || !programacaoValida(draft.programacao, professor)) {
      setErro('Revise os dias e horários da programação semanal.');
      return;
    }

    try {
      await criarMatricula({
        alunoId,
        dados: {
          professorId: draft.professorId,
          materiaId: draft.materiaId,
          estagio: draft.estagio || undefined,
          tipoAtendimento: draft.tipoAtendimento,
          observacoes: draft.observacoes || undefined,
          horarios: Object.entries(draft.programacao)
            .filter(([, valor]) => valor.frequenta)
            .map(([diaSemana, valor]) => ({
              diaSemana: diaSemana as keyof typeof draft.programacao,
              horario: valor.horario,
            })),
        },
      });

      onCriada();
    } catch (error) {
      setErro(error instanceof Error ? error.message : 'Não foi possível criar a matrícula.');
    }
  }

  return (
    <div className="space-y-3">
      <MatriculaDraftCard
        draft={draft}
        materias={materias}
        onChange={setDraft}
        onRemover={onCancelar}
      />
      {erro ? <p className="text-sm text-destructive">{erro}</p> : null}
      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={onCancelar}>
          Cancelar
        </Button>
        <Button type="button" onClick={() => void aoSalvar()} disabled={criandoMatricula}>
          {criandoMatricula ? 'Salvando...' : 'Salvar matrícula'}
        </Button>
      </div>
    </div>
  );
}
